// ItemPriceUpdatePage.jsx
// The Item Creation screen with only the price editable: pick an item from the list, change its
// standard price and save. Every other field is shown read-only, and the server rejects the
// request if any of them differs from what is stored.
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, Edit, Search, SellOutlined, TouchAppOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import useAppSettings, { hasDecimals } from "./useAppSettings";

const EMPTY_FORM = {
  itemName: "",
  unitName: "",
  standardPrice: "",
  barcode: "",
  itemCode: "",
  taxRate: "",
  hsnCode: "",
};

const formatPrice = (v) => (v === null || v === undefined || v === "" ? "" : Number(v).toFixed(2));

const blank = (v) => (v === null || v === undefined ? "" : v);

async function errorText(res) {
  try {
    const data = await res.json();
    return data?.errorMessage || data?.message || `${res.status} ${res.statusText}`;
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}

const ItemPriceUpdatePage = () => {
  const { t } = useTranslation();
  const tenancyId = localStorage.getItem("tenancyId");

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortField, setSortField] = useState("itemName");
  const [totalItems, setTotalItems] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(null); // the item as loaded from the list
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { settings } = useAppSettings();
  const priceDecimals = settings.itemPriceUpdatePriceDecimals;
  const priceRef = useRef(null);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, page, rowsPerPage, sortOrder, sortField]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/api/${tenancyId}/items-search?query=${encodeURIComponent(searchQuery)}&page=${page}&size=${rowsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` } }
      );
      if (!response.ok) {
        setItems([]);
        setTotalItems(0);
        return;
      }
      const data = await response.json();
      setItems(data.content || []);
      setTotalItems(data.totalElements || 0);
    } catch (error) {
      console.error("Error fetching items:", error);
      setItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePick = (item) => {
    setSelected(item);
    setFormData({
      itemName: blank(item.itemName),
      unitName: blank(item.unitName),
      standardPrice: blank(item.standardPrice),
      barcode: blank(item.barcode),
      itemCode: blank(item.itemCode),
      taxRate: blank(item.taxRate),
      hsnCode: blank(item.hsnCode),
    });
    setErrorMessage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      priceRef.current?.focus();
      priceRef.current?.select();
    }, 0);
  };

  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortField(field);
    setPage(0);
  };

  const handleCancel = () => {
    setSelected(null);
    setFormData(EMPTY_FORM);
    setErrorMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;

    const price = Number(formData.standardPrice);
    if (formData.standardPrice === "" || !Number.isFinite(price) || price <= 0) {
      setErrorMessage(t("positivePrice", "Price must be greater than zero."));
      return;
    }
    if (!priceDecimals && hasDecimals(price)) {
      setErrorMessage("Decimal prices are turned off in Settings. Enter a whole number.");
      return;
    }
    if (Math.round(price * 100) / 100 !== price) {
      setErrorMessage("Price can have at most 2 decimal places.");
      return;
    }

    // Send the item as it was loaded with only the price changed; the server refuses the
    // request if any other field no longer matches what is stored.
    const body = {
      itemName: selected.itemName,
      unitName: selected.unitName,
      barcode: selected.barcode,
      itemCode: selected.itemCode,
      taxRate: selected.taxRate,
      hsnCode: selected.hsnCode,
      standardPrice: price,
    };

    setSaving(true);
    setErrorMessage(null);
    try {
      const response = await fetch(`/api/${tenancyId}/pricing/item-master-price/${selected.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        setErrorMessage(await errorText(response));
        return;
      }
      setSuccessMessage(`Price of ${selected.itemName} updated to ${price.toFixed(2)}.`);
      setSelected(null);
      setFormData(EMPTY_FORM);
      fetchItems();
    } catch (error) {
      console.error("Error updating price:", error);
      setErrorMessage(t("updateFailed", "Could not update the price."));
    } finally {
      setSaving(false);
    }
  };

  const oldPrice = selected ? Number(selected.standardPrice) : null;
  const newPrice = formData.standardPrice === "" ? null : Number(formData.standardPrice);
  const change =
    selected && newPrice !== null && newPrice > 0 && Number.isFinite(newPrice) && Number.isFinite(oldPrice) && newPrice !== oldPrice
      ? newPrice - oldPrice
      : null;

  const details = [
    [t("unitName"), formData.unitName],
    [t("itemCode"), formData.itemCode],
    [t("barcode"), formData.barcode],
    [t("hsnCode"), formData.hsnCode],
    [t("taxRate"), formData.taxRate === "" ? "" : `${formData.taxRate}%`],
  ];

  const sortable = (field, label, align) => (
    <TableCell align={align} sortDirection={sortField === field ? sortOrder : false}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortOrder : "asc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const hideOnPhone = { display: { xs: "none", sm: "table-cell" } };

  return (
    <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: { xs: 2, sm: 3 } }}>
        <SellOutlined color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            Item Price Update
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pick an item from the list and change its standard price. Other details can't be changed here.
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={3} alignItems="flex-start">
        {/* Price form */}
        <Grid item xs={12} md={5}>
          <Paper
            variant="outlined"
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, position: { md: "sticky" }, top: { md: 80 } }}
          >
            {!selected ? (
              <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 6 }, textAlign: "center" }}>
                <TouchAppOutlined color="action" sx={{ fontSize: 44 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Choose an item
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                  Click an item in the list to change its price. Use the search box to find it quickly.
                </Typography>
              </Stack>
            ) : (
              <>
                <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.08em" }}>
                      Changing price of
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {formData.itemName}
                    </Typography>
                  </Box>
                  <Tooltip title="Cancel">
                    <IconButton size="small" onClick={handleCancel} aria-label="Cancel" disabled={saving}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {errorMessage && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
                    {errorMessage}
                  </Alert>
                )}

                <Box
                  component="dl"
                  sx={{
                    m: 0,
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    columnGap: 2,
                    rowGap: 0.75,
                    "& dt": { color: "text.secondary", typography: "body2" },
                    "& dd": { m: 0, typography: "body2", fontWeight: 500, wordBreak: "break-all" },
                  }}
                >
                  {details.map(([label, value]) => (
                    <React.Fragment key={label}>
                      <dt>{label}</dt>
                      <dd>{value === "" ? "—" : value}</dd>
                    </React.Fragment>
                  ))}
                </Box>

                <Divider sx={{ my: 2.5 }} />

                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Current price"
                      value={formatPrice(selected.standardPrice)}
                      InputProps={{ readOnly: true }}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      required
                      fullWidth
                      label="New price"
                      name="standardPrice"
                      type="number"
                      inputRef={priceRef}
                      value={formData.standardPrice}
                      onChange={(e) => {
                        setFormData((f) => ({ ...f, standardPrice: e.target.value }));
                        if (errorMessage) setErrorMessage(null);
                      }}
                      error={Boolean(errorMessage)}
                      inputProps={{ min: "0", step: priceDecimals ? "0.01" : "any", inputMode: priceDecimals ? "decimal" : "numeric" }}
                      helperText={
                        change !== null ? (
                          <Box component="span" sx={{ color: change > 0 ? "success.main" : "warning.main" }}>
                            {change > 0 ? "+" : "−"}
                            {Math.abs(change).toFixed(2)}
                            {oldPrice > 0 ? ` (${change > 0 ? "+" : "−"}${Math.abs((change / oldPrice) * 100).toFixed(1)}%)` : ""}
                          </Box>
                        ) : priceDecimals ? (
                          ""
                        ) : (
                          "Whole numbers only (decimals are off in Settings)."
                        )
                      }
                    />
                  </Grid>
                </Grid>

                <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
                  <Button variant="outlined" color="inherit" onClick={handleCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Edit />}
                    sx={{ minWidth: 160 }}
                  >
                    Update Price
                  </Button>
                </Stack>
              </>
            )}
          </Paper>
        </Grid>

        {/* Item list */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1.5}
              sx={{ p: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("listOfItems")}
                </Typography>
                <Chip size="small" label={totalItems} />
              </Stack>
              <TextField
                size="small"
                autoFocus
                placeholder="Search by name or code"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: { sm: 260 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchQuery("");
                          setPage(0);
                        }}
                        aria-label="Clear search"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Stack>
            <Box sx={{ height: 4 }}>{loading && <LinearProgress />}</Box>
            <TableContainer>
              <Table size="small" sx={{ "& th, & td": { px: { xs: 1, sm: 2 } } }}>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
                    {sortable("itemName", t("itemName"))}
                    <TableCell sx={hideOnPhone}>{t("itemCode")}</TableCell>
                    <TableCell>{t("unitName")}</TableCell>
                    {sortable("standardPrice", t("standardPrice"), "right")}
                    <TableCell align="right">{t("actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      selected={selected?.id === item.id}
                      onClick={() => handlePick(item)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.itemName}
                        </Typography>
                        {item.barcode && (
                          <Typography variant="caption" color="text.secondary">
                            {item.barcode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={hideOnPhone}>{item.itemCode}</TableCell>
                      <TableCell>{item.unitName}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatPrice(item.standardPrice)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Change price">
                          <IconButton
                            size="small"
                            aria-label="Change price"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePick(item);
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        {searchQuery ? `No items match "${searchQuery}".` : "No items found."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccessMessage(null)} sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ItemPriceUpdatePage;
